import { individualsService, LoginContext, RefreshOutline } from '@aslanpay/portal-common-lib';
import { Button, MenuItem } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';

import { unauthenticatedPaths } from 'app/constants/routes';
import useAuthContext from 'app/hooks/useAuthContext';
import useResolveEmployeeSwitch from 'app/hooks/useResolveEmployeeSwitch';
import useSsoContextSwitch from 'app/hooks/useSsoContextSwitch';
import { useAuthenticationStore } from 'app/store';

const PORTAL_LABELS: Record<LoginContext, string> = {
  ADMIN: 'admin',
  EMPLOYEE: 'employee',
};

const MUTED_ICON = 'brand.mutedIcon';
const MUTED_TEXT = 'brand.menuText';

interface PortalSwitcherMenuItemProps {
  /** 'row' renders a plain button for contexts with no Menu ancestor, like a bottom sheet. */
  renderAs?: 'menuItem' | 'row';
  /** 'muted' greys the icon back, for menus where the labels do the work. */
  tone?: 'default' | 'muted';
}

function PortalSwitcherMenuItem({
  renderAs = 'menuItem',
  tone = 'default',
}: PortalSwitcherMenuItemProps) {
  const navigate = useNavigate();
  const setAuthenticationData = useAuthenticationStore((state) => state.setAuthenticationData);
  const { activeLoginContext, availableLoginContexts, switchLoginContext, user } = useAuthContext();
  const startSsoSwitch = useSsoContextSwitch();
  const { resolveEmployeeSwitch } = useResolveEmployeeSwitch();

  const targetContext: LoginContext = activeLoginContext === 'ADMIN' ? 'EMPLOYEE' : 'ADMIN';
  const target = availableLoginContexts.find(({ context }) => context === targetContext);

  if (!target) {
    return null;
  }

  const startPasswordSwitch = () => {
    setAuthenticationData({
      email: user?.email ?? '',
      loginContext: targetContext,
      isContextSwitch: true,
    });
    navigate(unauthenticatedPaths.authentication);
  };

  /* An INVITED identity's checkEmailStep call sends a verification code as a side effect, so that
     must only happen once the admin explicitly confirms - not silently on this menu click. This
     read-only, company-scoped lookup (the same one the employees list uses) lets us tell upfront
     whether that's the case, without resolving (and sending) anything yet. Every other outcome has
     no side effect worth gating, so it resolves immediately as before. */
  const startEmployeeSwitch = async () => {
    const email = user?.email ?? '';

    try {
      // company-manager's IndividualFilterParams.setEmail expects a '+' to already be the literal
      // text "%2B" by the time it reaches that setter (an API Gateway quirk) and URL-decodes on top
      // of that - a normal single-encoded '+' silently becomes a space server-side and matches
      // nothing. Pre-encoding it here so a "+alias@..." email (common for test accounts) resolves.
      const { list } = await individualsService.fetchIndividuals({
        email: [email.replace(/\+/g, '%2B')],
        role: ['EMPLOYEE'],
        page: 0,
        size: 1,
      });

      if (list[0]?.status === 'INVITED') {
        setAuthenticationData({
          email,
          loginContext: 'EMPLOYEE',
          isContextSwitch: true,
          contextSwitchStep: 'employeeSwitchConfirm',
        });
        navigate(unauthenticatedPaths.authentication);
        return;
      }
    } catch {
      // Fall through to the full resolution below, which handles its own errors - this lookup is
      // only an optimisation to avoid the confirm screen for the common instant cases.
    }

    await resolveEmployeeSwitch(email);
  };

  const handleSwitch = async () => {
    if (await switchLoginContext(targetContext)) {
      navigate('/', { replace: true });
      return;
    }

    if (targetContext === 'EMPLOYEE') {
      await startEmployeeSwitch();
      return;
    }

    if (target.authenticationMethod === 'SSO_ENTRA') {
      await startSsoSwitch(target);
      return;
    }

    startPasswordSwitch();
  };

  const label = `Switch to ${PORTAL_LABELS[targetContext]} portal`;
  const isMuted = tone === 'muted';
  const icon = (
    <RefreshOutline
      boxSize={renderAs === 'row' ? '18px' : '16px'}
      color={isMuted ? MUTED_ICON : undefined}
    />
  );
  const labelColor = isMuted ? MUTED_TEXT : 'brand.darkPurple';

  if (renderAs === 'row') {
    return (
      <Button
        data-testid="portal-switcher-menu-item"
        onClick={handleSwitch}
        leftIcon={icon}
        variant="ghost"
        justifyContent="flex-start"
        w="full"
        minH="48px"
        borderRadius="8px"
        fontWeight="medium"
        color={labelColor}
        _hover={{ bg: 'brand.lightGray2' }}
      >
        {label}
      </Button>
    );
  }

  return (
    <MenuItem
      data-testid="portal-switcher-menu-item"
      onClick={handleSwitch}
      icon={icon}
      color={labelColor}
      _hover={{ bg: 'brand.lightGray2' }}
    >
      {label}
    </MenuItem>
  );
}

export default PortalSwitcherMenuItem;
